// 导入必要的React和Material-UI组件
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useLocation } from "react-router-dom";

// 样式化组件 - 移除hover效果（不可点击）
const StyledCard = styled(Card)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: 20,
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  marginBottom: theme.spacing(4),
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 20,
    pointerEvents: "none",
  },
  // 移除hover效果 - 卡片不可点击
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  color: "rgba(255, 255, 255, 0.95)",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
}));

const StyledAccordion = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  borderRadius: "12px !important",
  "&:before": { display: "none" },
  boxShadow: "none",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  marginTop: theme.spacing(2),
  "& .MuiAccordionSummary-root": {
    minHeight: 48,
    padding: "0 24px",
    borderRadius: 12,
    "&:hover": {
      background: "rgba(255, 255, 255, 0.05)",
    },
  },
  "& .MuiAccordionSummary-content": {
    margin: "12px 0",
  },
  "& .MuiTypography-root": {
    color: "rgba(255, 255, 255, 0.75)",
    fontSize: "1rem",
  },
  "& .MuiSvgIcon-root": {
    color: "rgba(255, 255, 255, 0.75)",
    fontSize: "1.5rem",
  },
  "& .MuiAccordionDetails-root": {
    padding: "16px 24px 24px",
  },
}));

// 侧边导航项目 - 主菜单
const SideNavItem = styled(ListItem)(({ theme, active }) => ({
  borderRadius: "8px",
  marginBottom: theme.spacing(0.5),
  transition: "all 0.2s ease",
  padding: theme.spacing(1.5, 2),
  cursor: "pointer",
  position: "relative",
  ...(active && {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "60%",
      backgroundColor: "rgba(255, 255, 255, 0.6)",
      borderRadius: "0 2px 2px 0",
    },
  }),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
}));

// 侧边导航项目 - 子菜单
const SubNavItem = styled(ListItem)(({ theme, active }) => ({
  borderRadius: "8px",
  marginBottom: theme.spacing(0.5),
  transition: "all 0.2s ease",
  padding: theme.spacing(1, 2, 1, 3.5),
  cursor: "pointer",
  position: "relative",
  fontSize: "0.85rem",
  ...(active && {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 16,
      top: "50%",
      transform: "translateY(-50%)",
      width: "2px",
      height: "50%",
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      borderRadius: "0 2px 2px 0",
    },
  }),
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },
}));

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 默认内容（如果API获取失败时使用）
const defaultIntroSections = [
  {
    id: 1,
    title: "1. 3C基督徒联盟是什么？",
    descriptions: [
      "它是一个线上与线下相结合的基督徒生活与事奉的平台；",
      "在这里，有志服侍的弟兄姐妹，可以找到合适的人群来进行可持续的事奉，也可以得到平台的支持和牧养；",
      "在这里，希望得到牧养的弟兄姐妹，可以获得真理正统、生命敬虔的服侍，更可以与其他弟兄姐妹一起清楚自己的呼召，发现和培养自己的恩赐，参与到事奉中来；",
      "在这里，将没有时间的阻隔、地域的阻隔、也将没有资源的局限、教派的局限。",
    ],
  },
  {
    id: 2,
    title: "2. 3C基督徒联盟的异象是什么？",
    descriptions: ["我们的异象是人人领受异象；每个人都清楚自己的召命是什么。"],
  },
  {
    id: 3,
    title: "3. 3C基督徒联盟的策略是什么？",
    descriptions: [
      "1、人人领受呼召（Calling）",
      "2、平台连接你我（Connecting）",
      "3、教会关顾众人（Caring）",
    ],
  },
  {
    id: 4,
    title: "4. 3C基督徒联盟的认信信条是什么？",
    descriptions: [
      "我们相信新旧约圣经全是 神的启示，完全由圣灵感动作者写成，原本毫无错误，是信仰与生活的最高准则。",
      "我们相信独一真神是永存的圣父、圣子、圣灵，创造、统管并扶持万有的主宰。",
      "我们相信耶稣基督是 神的独生子，由圣灵感孕童女马里亚所生，舍身流血为世人赎罪；衪从死里复活升天，并照衪的应许将再降临。",
      "我们相信圣灵与圣父、圣子同质、同权、同存，衪在教会中运行、同工、引导信者进入真理。",
      "我们相信人是照神的形像造成的，在亚当里堕落成为罪人，需要神的救恩。",
      "我们相信悔改的罪人，接受耶稣始得重生，并因信仰耶稣基督得以称义。",
      "我们相信凡称义的人能因圣灵成圣，活出圣洁的生活。",
      "我们相信信者身体复活得永生，不信之人复活受永刑。",
      "我们相信教会是一体，基督是元首，由圣灵重生者所组成，应在敬拜、交通及事奉上彼此相通。",
      "我们相信家庭是上帝所看重的，是事奉上帝的基本单位（约书亚记24:15），婚姻是神宣告为一男一女在盟约承诺中的联合，合法的性关系是仅限于婚姻关系以内。我们相信儿女是上帝的赐福，更是托付，他们对上帝的认识和跟随是我们的第一养育责任。",
    ],
  },
];

// 去掉标题中的数字前缀（如 "1. "）
const removeNumberPrefix = (title) => {
  return title.replace(/^\d+\.\s*/, "");
};

// 重组数据，创建层次结构
const organizeMenuItems = (introSections) => {
  const mainItems = [];

  introSections.forEach((section) => {
    const id = section.id || section.sectionId;
    // 只添加整数ID的顶级标题
    if (Number.isInteger(id)) {
      mainItems.push({
        id: id,
        title: removeNumberPrefix(section.title),
        sectionId: `section-${id}`,
      });
    }
  });

  return { mainItems };
};

const Intro = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeSection, setActiveSection] = useState("section-1");
  const [introSections, setIntroSections] = useState(defaultIntroSections);
  const sectionRefs = useRef([]);
  const navRef = useRef(null);

  // 获取简介内容
  useEffect(() => {
    const fetchIntroContent = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/intro-content`);
        if (response.data && response.data.length > 0) {
          // 将数据库格式转换为页面显示格式
          const formattedSections = response.data
            .sort((a, b) => a.sectionId - b.sectionId)
            .map((item) => ({
              id: item.sectionId,
              title: item.title,
              descriptions: item.descriptions,
            }));
          setIntroSections(formattedSections);
        }
      } catch (err) {
        console.error("获取简介内容失败:", err);
        // 如果获取失败，使用默认内容
        setIntroSections(defaultIntroSections);
      }
    };

    fetchIntroContent();
  }, []);

  // 获取组织好的菜单项
  const { mainItems } = organizeMenuItems(introSections);

  // 处理侧边导航点击
  const handleNavClick = (sectionId, index) => {
    setActiveSection(`section-${sectionId}`);

    // 找到完全匹配ID的内容部分
    const targetIndex = introSections.findIndex(
      (section) => section.id === sectionId
    );
    if (targetIndex !== -1 && sectionRefs.current[targetIndex]) {
      const yOffset = -100; // 偏移量，使章节在视窗中间
      const element = sectionRefs.current[targetIndex];
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y }); // 移除 behavior: "smooth"
    }
  };

  // 移除滚动监听，只保留从其他页面跳转的处理
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          const yOffset = -100;
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y });
          setActiveSection(id);
        }, 300);
      }
    }
  }, [location]);

  return (
    <Container
      maxWidth="lg"
      sx={{
        pt: { xs: 4, md: 6 },
        pb: { xs: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* 页面标题 */}
      <Typography
        variant="h4"
        sx={{
          textAlign: "center",
          color: "rgba(255, 255, 255, 0.95)",
          mb: 6,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          display: { xs: "none", md: "block" },
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
        }}
      >
        认识我们
      </Typography>

      <Grid container spacing={4}>
        {/* 左侧导航 */}
        <Grid item xs={12} md={3}>
          <StyledCard
            sx={{
              position: "sticky",
              top: 100,
              maxHeight: "calc(100vh - 150px)",
              overflow: "auto",
            }}
          >
            <CardContent
              sx={{ p: 2, position: "relative", zIndex: 1 }}
              ref={navRef}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  pl: 1,
                  color: "rgba(255, 255, 255, 0.9)",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                }}
              >
                目录
              </Typography>
              <Divider
                sx={{ mb: 2, borderColor: "rgba(255, 255, 255, 0.2)" }}
              />
              <List sx={{ p: 0 }}>
                {mainItems.map((item, index) => (
                  <SideNavItem
                    key={item.id}
                    active={activeSection === item.sectionId}
                    onClick={() => handleNavClick(item.id, index)}
                    data-section={item.sectionId}
                  >
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: "0.9rem",
                        fontWeight:
                          activeSection === item.sectionId ? 500 : 400,
                        color: "rgba(255, 255, 255, 0.9)",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    />
                    {activeSection === item.sectionId && (
                      <ArrowForwardIosIcon
                        sx={{ fontSize: 12, color: "rgba(255, 255, 255, 0.7)" }}
                      />
                    )}
                  </SideNavItem>
                ))}
              </List>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* 右侧内容 */}
        <Grid item xs={12} md={9}>
          {/* 内容区域 */}
          {introSections.map((section, index) => (
            <StyledCard
              key={section.id}
              id={`section-${section.id}`}
              ref={(el) => (sectionRefs.current[index] = el)}
            >
              <CardContent
                sx={{ p: { xs: 2.5, md: 4 }, position: "relative", zIndex: 1 }}
              >
                <SectionTitle
                  variant={isMobile ? "h6" : "h5"}
                  sx={{
                    mb: 3,
                    fontSize: { xs: "1.1rem", md: "1.4rem" },
                  }}
                >
                  <InfoIcon sx={{ color: "rgba(255, 255, 255, 0.8)" }} />
                  {removeNumberPrefix(section.title)}
                </SectionTitle>

                <Box sx={{ ml: { xs: 0, md: 4 } }}>
                  {section.descriptions.map((description, index) => (
                    <Typography
                      key={index}
                      sx={{
                        mb: 2,
                        color: "rgba(255, 255, 255, 0.85)",
                        fontSize: { xs: "0.95rem", md: "1rem" },
                        lineHeight: 1.7,
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      {description}
                    </Typography>
                  ))}

                  {/* {section.videoUrl && (
                    <StyledAccordion>
                      <Box sx={{ p: 2 }}>
                        <Typography
                          sx={{
                            color: "rgba(0, 0, 0, 0.9)",
                            mb: 2,
                            fontWeight: 500,
                          }}
                        >
                          观看相关视频
                        </Typography>
                        <Box
                          sx={{
                            position: "relative",
                            paddingBottom: "56.25%",
                            height: 0,
                            overflow: "hidden",
                            borderRadius: 2,
                          }}
                        >
                          <iframe
                            src={section.videoUrl}
                            title={`Video for ${section.title}`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                      </Box>
                    </StyledAccordion>
                  )} */}
                </Box>
              </CardContent>
            </StyledCard>
          ))}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Intro;
